import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tab,
  Tabs,
  Alert,
  AlertTitle,
  CircularProgress,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  DirectionsBoat as BoatIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  History as HistoryIcon,
  Today as TodayIcon,
  ConfirmationNumber as TicketIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getReservationsByCustomer } from '../../firebase/services/reservationService';
import { Reservation } from '../../models/types';
import { format, isPast, isToday } from 'date-fns';
import { formatTime12Hour } from '../../utils/formatters';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const CustomerDashboard: React.FC = () => {
  const { userProfile, currentUser } = useAuth();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState<any>(null);

  // Check for success message in location state (coming from reservation completion)
  useEffect(() => {
    if (location.state && (location.state as any).reservationSuccess) {
      setShowSuccess(true);
      setSuccessData(location.state);
      
      // Clear the state after showing the message
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Load reservations
  useEffect(() => {
    const fetchReservations = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const fetchedReservations = await getReservationsByCustomer(currentUser.id);
        setReservations(fetchedReservations);
      } catch (err) {
        console.error('Error fetching reservations:', err);
        setError('Failed to load your reservations. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReservations();
  }, [currentUser]);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Filter reservations by status
  const upcomingReservations = reservations.filter(res => 
    !isPast(new Date(res.date)) || isToday(new Date(res.date))
  );
  
  const pastReservations = reservations.filter(res => 
    isPast(new Date(res.date)) && !isToday(new Date(res.date))
  );

  // Reservation card component
  const ReservationCard = ({ reservation }: { reservation: Reservation }) => {
    const isPastReservation = isPast(new Date(reservation.date)) && !isToday(new Date(reservation.date));
    const isTodays = isToday(new Date(reservation.date));
    
    // Function to capitalize first letter of each word
    const capitalizeWords = (str: string) => {
      return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
    };
    
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box key="vessel-info" sx={{ display: 'flex', alignItems: 'center' }}>
              <BoatIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" component="div">
                {capitalizeWords(reservation.vesselType)}
              </Typography>
            </Box>
            <Chip 
              key="status-chip"
              icon={isTodays ? <TodayIcon /> : isPastReservation ? <HistoryIcon /> : <TicketIcon />} 
              label={isTodays ? 'Today' : isPastReservation ? 'Past' : capitalizeWords(reservation.status)}
              color={
                isTodays ? 'warning' : 
                isPastReservation ? 'default' : 
                reservation.status === 'confirmed' ? 'success' : 
                reservation.status === 'pending' ? 'primary' : 
                'error'
              }
              size="small"
            />
          </Box>
          
          <List dense disablePadding>
            <ListItem key="date-time" disablePadding sx={{ pb: 1 }}>
              <ListItemText 
                primary={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarIcon key="calendar-icon" fontSize="small" sx={{ mr: 1 }} />
                    <Typography key="date-time-text" variant="body2" fontWeight="medium">
                      Date & Time
                    </Typography>
                  </Box>
                } 
                secondary={`${format(new Date(reservation.date), 'EEEE, MMMM d, yyyy')} • ${formatTime12Hour(reservation.startTime)} - ${formatTime12Hour(reservation.endTime)}`} 
                secondaryTypographyProps={{ sx: { fontWeight: isTodays ? 'bold' : 'regular' } }}
              />
            </ListItem>
            
            <ListItem key="passengers" disablePadding sx={{ pb: 1 }}>
              <ListItemText 
                primary={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    <GroupIcon key="group-icon" fontSize="small" sx={{ mr: 1 }} />
                    <Typography key="passengers-text" variant="body2" fontWeight="medium">
                      Passengers
                    </Typography>
                  </Box>
                }
                secondary={`${reservation.partySize} ${reservation.partySize === 1 ? 'person' : 'people'}`} 
              />
            </ListItem>
            
            <ListItem key="reservation-id" disablePadding>
              <ListItemText 
                primary={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    <TicketIcon key="ticket-icon" fontSize="small" sx={{ mr: 1 }} />
                    <Typography key="reservation-id-text" variant="body2" fontWeight="medium">
                      Reservation ID
                    </Typography>
                  </Box>
                }
                secondary={reservation.id} 
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 1.5 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography key="price" variant="body1" fontWeight="medium" color="primary">
              ${reservation.totalPrice} +Fuel and Tax
            </Typography>
            
            {!isPastReservation && (
              <Button 
                key="view-details"
                size="small" 
                variant="outlined"
                component={Link}
                to={`/customer/reservation-details/${reservation.id}`}
              >
                View Details
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          My Dashboard
        </Typography>
        
        {userProfile && (
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Welcome, {userProfile.firstName}!
          </Typography>
        )}
        
        {/* Success message */}
        {showSuccess && successData && (
          <Alert 
            severity="success" 
            sx={{ mt: 2, mb: 4 }}
            onClose={() => setShowSuccess(false)}
          >
            <AlertTitle>Reservation Confirmed!</AlertTitle>
            Your reservation for <strong>{successData.vesselName}</strong> on {format(new Date(successData.date), 'MMMM d, yyyy')} has been successfully booked.
          </Alert>
        )}
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="reservation tabs">
            <Tab key="upcoming-tab" label="Upcoming Reservations" icon={<CalendarIcon />} iconPosition="start" {...a11yProps(0)} />
            <Tab key="past-tab" label="Past Reservations" icon={<HistoryIcon />} iconPosition="start" {...a11yProps(1)} />
          </Tabs>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TabPanel value={tabValue} index={0}>
              {upcomingReservations.length > 0 ? (
                upcomingReservations.map(reservation => (
                  <ReservationCard key={reservation.id} reservation={reservation} />
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CalendarIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No Upcoming Reservations
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    You don't have any upcoming boat reservations.
                  </Typography>
                </Box>
              )}
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              {pastReservations.length > 0 ? (
                pastReservations.map(reservation => (
                  <ReservationCard key={reservation.id} reservation={reservation} />
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <HistoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No Past Reservations
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    You don't have any past boat rentals with us yet.
                  </Typography>
                </Box>
              )}
            </TabPanel>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default CustomerDashboard; 